import useLocalStorage from 'hooks/useLocalStorage';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useMemo } from 'react';
import {
  DISABLE_PAGE_LIMIT,
  FieldFilter,
  NullOperatorValues,
  QueryFilterOperators,
} from 'types/api';
import { HandlingRequestSchema } from 'types/clientV2/stock';
import { HandlingRequestType } from 'types/inventory';
import {
  warehouseStorageName,
  warehouseDefaults,
} from 'types/warehouseSettings';
import { useGetHandlingRequestsV2Query } from 'store/slices/clientV2/handlingRequests';
import {
  MobilePickingHandlingRequest,
  PickListStockLot,
} from 'views/warehouse/MobilePicking/types';

export const useGetStockLotsToPick = () => {
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const {
    data: handlingRequestsResponse,
    isFetching: isFetchingHandlingRequests,
    isLoading: isLoadingHandlingRequests,
    refetch: refetchHandlingRequests,
  } = useServerSideQuery<HandlingRequestSchema, MobilePickingHandlingRequest>(
    useGetHandlingRequestsV2Query,
    {
      pageSize: DISABLE_PAGE_LIMIT,
      schema: [
        'id',
        'requestType',
        'stockLot',
        'kitRequestIdViaLot',
        'org',
        { stockLot: ['id', 'stockLocation', 'lotId', 'part', 'team', 'org'] },
      ],
      excludes: [
        {
          field: 'requestType',
          operator: QueryFilterOperators.isAnyOf,
          value: [HandlingRequestType.CONSUME],
        },
      ],
      filters: [
        {
          field: 'pickedAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
        {
          // eslint-disable-next-line no-restricted-syntax
          field: 'cancelledAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
        {
          field: 'completedAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
        {
          field: 'approvedAt',
          operator: QueryFilterOperators.isNotEmpty,
          value: NullOperatorValues.isNotEmpty,
        },
        {
          field: 'stockLot.stockLocation.facility',
          operator: QueryFilterOperators.isAnyOf,
          value: [settings.facility],
        },
        {
          field: 'stockLot.deletedAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
      ].filter(Boolean) as [
        FieldFilter<HandlingRequestSchema>,
        ...('and' | 'or' | FieldFilter<HandlingRequestSchema>)[],
        FieldFilter<HandlingRequestSchema>
      ],
    }
  );

  const stockLotLocationsIds = useMemo(() => {
    const handlingRequests = handlingRequestsResponse?.data || [];
    const ids = new Set<string>();
    handlingRequests.forEach((request) => {
      if (request.stockLot.stockLocation) {
        ids.add(request.stockLot.stockLocation);
      }
    });
    return Array.from(ids);
  }, [handlingRequestsResponse?.data]);

  const { stockLotsToPick, stockLotsToPickMap } = useMemo((): {
    stockLotsToPick: PickListStockLot[];
    stockLotsToPickMap: Map<string, PickListStockLot>;
  } => {
    const handlingRequests = handlingRequestsResponse?.data || [];
    if (!handlingRequests?.length)
      return { stockLotsToPick: [], stockLotsToPickMap: new Map() };

    // Create a Map for O(1) lookups and to maintain insertion order
    const stockLotMap = new Map<string, PickListStockLot>();

    // Build the map with all stock lots
    for (const request of handlingRequests) {
      const stockLotId = request.stockLot.id;

      if (!stockLotMap.has(stockLotId)) {
        stockLotMap.set(stockLotId, {
          ...request.stockLot,
          handlingRequests: [],
        });
      }

      // Only add the request if it's not already in the array
      const stockLot = stockLotMap.get(stockLotId)!;
      if (!stockLot.handlingRequests.some((hr) => hr.id === request.id)) {
        stockLot.handlingRequests.push(request);
      }
    }

    return {
      stockLotsToPick: Array.from(stockLotMap.values()),
      stockLotsToPickMap: stockLotMap,
    };
  }, [handlingRequestsResponse?.data]);

  return {
    stockLotsToPick,
    stockLotsToPickMap,
    stockLotLocationsIds,
    isFetchingHandlingRequests,
    isLoadingHandlingRequests,
    refetchHandlingRequests,
  };
};
